import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Modal } from '@frontend/ui-kit/agromarket/components/Modal'
import { useGetApprovedRegions } from '@/api/kubik/credit'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { getUserRegion } from '@/logic/userRegion'
import {
  isFieldEvaluationReportDialogAlreadyShown,
  markFieldEvaluationReportDialogAsAlreadyShown,
} from '@/pages/Financing/components/FieldEvaluationReportDialogLauncher/helpers'
import type { TFieldEvaluationReportDialogLauncherProps } from '@/pages/Financing/components/FieldEvaluationReportDialogLauncher/types'
import type { RootState } from '@/redux/interfaces'
import { EFinancingProductId } from '@/types'
import { FieldEvaluationReportDialog } from './components/FieldEvaluationReportDialog/FieldEvaluationReportDialog'
import { OPEN_DIALOG_DELAY_MS } from './constants'

addModalToWellKnown({ FieldEvaluationReportDialog })

const mapState = (state: RootState) => ({
  userRegion: getUserRegion()(state),
})

export const FieldEvaluationReportDialogLauncher = ({ comment }: TFieldEvaluationReportDialogLauncherProps) => {
  const dispatch = useDispatch()
  const { userRegion } = useSelector(mapState)
  const [isAlreadyShown] = useState(() => isFieldEvaluationReportDialogAlreadyShown())

  const userRegionCode = userRegion?.code
  const hasOpenedRef = useRef(false)

  const { data: allowedRegions, isSuccess } = useGetApprovedRegions({
    productId: EFinancingProductId.REPORT_CTRL2GO,
  })

  const isAllowed = useMemo(
    () => Boolean(userRegionCode) && Boolean(allowedRegions?.approvedRegions?.includes(userRegionCode)),
    [allowedRegions, userRegionCode],
  )

  useEffect(() => {
    if (isAlreadyShown || !isSuccess || !isAllowed || hasOpenedRef.current) {
      return
    }

    const timer = setTimeout(() => {
      if (hasOpenedRef.current) return

      hasOpenedRef.current = true

      markFieldEvaluationReportDialogAsAlreadyShown()

      Modal.show(FieldEvaluationReportDialog, {
        comment,
      })
    }, OPEN_DIALOG_DELAY_MS)

    return () => clearTimeout(timer)
  }, [comment, dispatch, isAllowed, isAlreadyShown, isSuccess])

  return null
}
